import React, { useEffect, useState } from 'react';
import apiService from '../../services/api';
import useGameSquad from '../../hooks/useGameSquad';
import {
  apiErrorMessage,
  formatEventDate,
  formatEventTime,
  hasFinalScore,
  isPastEvent,
  isPredictionOpen,
  readPrediction,
} from '../../utils/gameSchedule';
import { checkInAction } from '../../utils/gameCards';
import {
  checkedInCountLabel,
  hasSquad,
  predictedCountLabel,
  predictionCell,
  scoreOrZero,
  splitPinnedYou,
  squadCheckInRows,
  squadPredictionRows,
  squadPredictionsMode,
  squadRosterRows,
  squadSwitcherVisible,
} from '../../utils/gamePredictions';
import ComposeMatch, { ReadonlyMatch } from './ComposeMatch';
import { Avatar, CheckIcon, Modal, PersonRow, RankBadge, TeamMark } from './gameBits';

const PREVIEW = 5;

function statusLabel(event) {
  if (hasFinalScore(event)) return 'Final';
  return isPredictionOpen(event) ? 'Upcoming' : 'Live';
}

function composingPrediction(event) {
  return !readPrediction(event) && !event.already_predicted && !hasFinalScore(event) && isPredictionOpen(event);
}

// The two teams as column headers, then the final score on a grey row under them, or the
// date and time while the game is still to come.
function Matchup({ event }) {
  const scored = hasFinalScore(event);
  return (
    <div className="games-outcome">
      <div className="games-outcome-teams">
        <span />
        <TeamMark name={event.home_name} logoUrl={event.home_logo} className="is-outcome" />
        <span />
        <TeamMark name={event.opponent_name} logoUrl={event.opponent_logo} className="is-outcome" />
      </div>
      {scored ? (
        <div className="games-outcome-row is-final is-bare">
          <span />
          <strong>{event.home_score}</strong>
          <em aria-hidden="true">–</em>
          <strong>{event.away_score}</strong>
        </div>
      ) : (
        <p className="games-sheet-when">{[formatEventDate(event), formatEventTime(event)].join(' · ')}</p>
      )}
    </div>
  );
}

// The squad's name, and the way to switch squads when the fan has more than one.
function SquadTitle({ squad, switcher, open, onToggle }) {
  const data = squad.data;
  if (!hasSquad(data)) return null;
  const canSwitch = switcher !== false && squadSwitcherVisible(data);
  return (
    <div className="games-squad-title">
      {canSwitch ? (
        <button type="button" className="games-squad-switch" aria-expanded={open} onClick={onToggle}>
          {data.squad.name}
          <svg viewBox="0 0 16 16" aria-hidden="true">
            <path d="M4 6.5 8 10.5 12 6.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      ) : (
        <span>{data.squad.name}</span>
      )}
      {squad.switchFailed ? <em className="games-note">Could not load that squad.</em> : null}
    </div>
  );
}

function SquadPicker({ squads, selectedId, onPick }) {
  return (
    <ul className="games-squad-picker" aria-label="Your squads">
      {squads.map((squad) => {
        const picked = String(squad.id) === String(selectedId);
        const counts = squad.checked_in != null && squad.predicted != null ? `${squad.checked_in} checked in · ${squad.predicted} predicted` : '';
        return (
          <li key={String(squad.id)}>
            <button type="button" aria-pressed={picked} onClick={() => onPick(squad.id)}>
              {squad.cover_url ? <img src={squad.cover_url} alt="" /> : <span className="games-squad-cover-empty" aria-hidden="true" />}
              <span className="games-squad-pick-copy">
                <strong>{squad.name}</strong>
                {counts ? <em>{counts}</em> : null}
              </span>
              {picked ? <CheckIcon className="games-meta-check" /> : null}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function ViewAll({ total, expanded, onToggle }) {
  if (total <= PREVIEW) return null;
  return (
    <button type="button" className="games-view-all" onClick={onToggle}>
      {expanded ? 'Show less' : `View all ${total}`}
    </button>
  );
}

function CheckInsSection({ squad, expanded, onToggle }) {
  const data = squad.data;
  if (!hasSquad(data)) return null;
  if (squad.switching) return <p className="games-note">Loading…</p>;
  const rows = squadCheckInRows(data);
  const roster = squadRosterRows(data, rows);
  const total = Number(data.squad.member_count) || roster.length;
  const shown = expanded ? roster : roster.slice(0, PREVIEW);
  const checked = {};
  rows.forEach((row) => {
    checked[String(row.user_id)] = true;
  });
  return (
    <section className="games-sheet-section">
      <div className="games-section-head">
        <strong>{checkedInCountLabel(rows.length, total)}</strong>
        <ViewAll total={roster.length} expanded={expanded} onToggle={onToggle} />
      </div>
      {rows.length ? (
        <div className="games-cluster" aria-hidden="true">
          {rows.slice(0, 3).map((row) => <Avatar key={String(row.user_id)} row={row} size={28} />)}
          {rows.length > 3 ? <span className="games-cluster-more">{`+${rows.length - 3}`}</span> : null}
        </div>
      ) : (
        <p className="games-note">No one from your squad has checked in yet.</p>
      )}
      {roster.length ? (
        <ul className="games-people">
          {shown.map((row) => (
            <PersonRow key={String(row.user_id)} row={row} right={checked[String(row.user_id)] ? <CheckIcon className="games-meta-check" aria-label="Checked in" /> : null} />
          ))}
        </ul>
      ) : null}
    </section>
  );
}

function PredictionsSection({ event, squad, youPredicted, expanded, onToggle }) {
  const data = squad.data;
  const mode = squadPredictionsMode(data, hasFinalScore(event), true);
  if (!hasSquad(data)) return <p className="games-note">Join a squad to see your squad’s picks.</p>;
  if (squad.switching) return <p className="games-note">Loading…</p>;
  if (!youPredicted && mode !== 'ranked') return <p className="games-note">Predict the score to see your squad’s picks.</p>;
  const rows = squadPredictionRows(data);
  if (mode === 'empty' || !rows.length) return <p className="games-note">No predictions from this squad yet</p>;
  const roster = squadRosterRows(data, rows);
  const total = Number(data.squad.member_count) || roster.length;
  const shown = expanded ? roster : roster.slice(0, PREVIEW);
  return (
    <section className="games-sheet-section">
      <div className="games-section-head">
        <strong>{predictedCountLabel(rows.length, total)}</strong>
        <ViewAll total={roster.length} expanded={expanded} onToggle={onToggle} />
      </div>
      <ul className="games-people">
        {shown.map((row) => (
          <PersonRow
            key={String(row.user_id)}
            row={row}
            tag={mode === 'ranked' && Number(row.rank) === 1 ? 'Closest' : ''}
            right={<span className="games-pick">{predictionCell(row)}</span>}
          />
        ))}
      </ul>
    </section>
  );
}

// After the final score: the outcome, then the squad's leaderboard with medals, your own row
// pinned under a divider when you sit past third.
function ResultsPanel({ event, squad }) {
  const mine = readPrediction(event);
  const data = squad.data;
  const rows = squadPredictionRows(data);
  const roster = hasSquad(data) ? squadRosterRows(data, rows) : [];
  const total = hasSquad(data) ? Number(data.squad.member_count) || roster.length : 0;
  const { rows: listed, pinned } = splitPinnedYou(roster);
  return (
    <div className="games-results-panel">
      <div className="games-outcome">
        <div className="games-outcome-teams">
          <span />
          <TeamMark name={event.home_name} logoUrl={event.home_logo} className="is-outcome" />
          <span />
          <TeamMark name={event.opponent_name} logoUrl={event.opponent_logo} className="is-outcome" />
        </div>
        <div className="games-outcome-rows">
          {mine ? (
            <div className="games-outcome-row">
              <span className="games-outcome-label">Your prediction</span>
              <strong>{mine.home}</strong>
              <em aria-hidden="true">–</em>
              <strong>{mine.away}</strong>
            </div>
          ) : null}
          <div className="games-outcome-row is-final">
            <span className="games-outcome-label">Final score</span>
            <strong>{event.home_score}</strong>
            <em aria-hidden="true">–</em>
            <strong>{event.away_score}</strong>
          </div>
        </div>
      </div>
      {!hasSquad(data) ? (
        <p className="games-note">Join a squad to see your squad’s picks.</p>
      ) : squad.switching ? (
        <p className="games-note">Loading…</p>
      ) : !rows.length ? (
        <p className="games-note">No predictions from this squad</p>
      ) : (
        <section className="games-sheet-section">
          <div className="games-section-head">
            <strong>{predictedCountLabel(rows.length, total)}</strong>
          </div>
          <ul className="games-people is-ranked">
            {listed.map((row) => (
              <PersonRow key={String(row.user_id)} row={row} lead={<RankBadge rank={row.rank} />} right={<span className="games-pick">{scoreOrZero(row)}</span>} />
            ))}
            {pinned ? (
              <>
                <li className="games-people-divider" aria-hidden="true" />
                <PersonRow row={pinned} lead={<RankBadge rank={pinned.rank} />} right={<span className="games-pick">{scoreOrZero(pinned)}</span>} />
              </>
            ) : null}
          </ul>
        </section>
      )}
    </div>
  );
}

// The game sheet of the embed's Games tab: a Predictions | Check-ins switch (always opening on
// Predictions), the squad's picks and check-ins, the inline score compose, and the footer
// button for whichever action the side allows.
export default function GameSheet({ event, mode = 'tabs', initialTab = 'predictions', squadId, switcher = true, onClose, onSaved, onChangePrediction, onCheckIn }) {
  const tabbed = mode === 'tabs';
  const [tab, setTab] = useState(tabbed ? initialTab : mode === 'checkins' ? 'checkins' : 'predictions');
  const [scores, setScores] = useState({ home: 0, away: 0 });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [expanded, setExpanded] = useState({ predictions: false, checkins: false });
  const squad = useGameSquad(event.id, squadId);

  useEffect(() => {
    setError('');
  }, [tab]);

  const final = hasFinalScore(event);
  const composing = tab === 'predictions' && composingPrediction(event);
  const mine = readPrediction(event);
  const youPredicted = !!(mine || event.already_predicted);
  const checkInOpen = !event.already_checked_in && checkInAction(event, Date.now(), isPastEvent(event));
  const showTabs = tabbed && hasSquad(squad.data);
  const side = showTabs ? tab : tabbed ? 'predictions' : tab;

  const save = async () => {
    if (saving) return;
    setSaving(true);
    setError('');
    try {
      const response = await apiService.createScheduleEventPrediction(event.id, { home_score: scores.home, away_score: scores.away });
      const saved = response?.prediction || { home_score: scores.home, away_score: scores.away };
      onSaved({ ...event, already_predicted: true, my_prediction: saved });
      if (hasSquad(squad.data)) squad.select(squad.selectedId);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not save prediction'));
    } finally {
      setSaving(false);
    }
  };

  const footer = side === 'predictions'
    ? composing
      ? { label: saving ? 'Saving…' : 'Confirm prediction', onClick: save, disabled: saving }
      : null
    : checkInOpen
      ? { label: 'Check in', onClick: () => onCheckIn(event) }
      : null;

  const head = () => {
    if (side === 'predictions' && final) return null;
    if (side === 'predictions' && composing) {
      return (
        <div className="games-sheet-head">
          <span className="games-badge">Prediction</span>
          <SquadTitle squad={squad} switcher={switcher} open={pickerOpen} onToggle={() => setPickerOpen((open) => !open)} />
          <h2>Predict the score</h2>
          <ComposeMatch event={event} scores={scores} onChange={setScores} />
        </div>
      );
    }
    if (side === 'predictions' && youPredicted) {
      return (
        <div className="games-sheet-head">
          {isPredictionOpen(event) ? (
            <button type="button" className="games-change" onClick={() => onChangePrediction(event)}>
              Change
            </button>
          ) : null}
          <span className="games-badge">Prediction</span>
          <SquadTitle squad={squad} switcher={switcher} open={pickerOpen} onToggle={() => setPickerOpen((open) => !open)} />
          <h2>Your prediction</h2>
          <p className="games-sheet-sub">
            {isPredictionOpen(event) ? 'Submitted · you can change it until game start time' : 'Submitted'}
          </p>
          <ReadonlyMatch event={event} scores={mine || { home: 0, away: 0 }} />
        </div>
      );
    }
    return (
      <div className="games-sheet-head is-game">
        <span className="games-pill">{statusLabel(event)}</span>
        <SquadTitle squad={squad} switcher={switcher} open={pickerOpen} onToggle={() => setPickerOpen((open) => !open)} />
        <h2 className="games-sheet-h">{side === 'checkins' ? 'Checked in' : 'Predictions'}</h2>
        <Matchup event={event} />
      </div>
    );
  };

  return (
    <Modal label={`${event.home_name || 'Home'} vs ${event.opponent_name || 'Away'}`} onClose={onClose} className="is-sheet">
      {showTabs ? (
        <div className="games-sheet-tabs" role="tablist">
          <button type="button" role="tab" aria-selected={side === 'predictions'} className={side === 'predictions' ? 'is-on' : ''} onClick={() => setTab('predictions')}>
            Predictions
          </button>
          <button type="button" role="tab" aria-selected={side === 'checkins'} className={side === 'checkins' ? 'is-on' : ''} onClick={() => setTab('checkins')}>
            Check-ins
          </button>
        </div>
      ) : null}
      <div className="games-sheet-body">
        {side === 'predictions' && final ? (
          <>
            {switcher !== false && squadSwitcherVisible(squad.data) ? (
              <SquadTitle squad={squad} switcher={switcher} open={pickerOpen} onToggle={() => setPickerOpen((open) => !open)} />
            ) : null}
            {pickerOpen && squad.data?.squads ? (
              <SquadPicker squads={squad.data.squads} selectedId={squad.selectedId} onPick={(id) => { setPickerOpen(false); squad.select(id); }} />
            ) : null}
            <div className="games-sheet-head is-results">
              <span className="games-predict-badge">Prediction</span>
              {hasSquad(squad.data) ? <span className="games-results-squad">{squad.data.squad.name}</span> : null}
              <h2 className="games-sheet-h">Prediction results</h2>
            </div>
            <ResultsPanel event={event} squad={squad} />
          </>
        ) : (
          <>
            {head()}
            {pickerOpen && squad.data?.squads ? (
              <SquadPicker squads={squad.data.squads} selectedId={squad.selectedId} onPick={(id) => { setPickerOpen(false); squad.select(id); }} />
            ) : null}
            {squad.loadFailed && !squad.data ? (
              <p className="games-note is-failed">
                Could not load your squad
                <button type="button" className="games-link" onClick={squad.retry}>Try again</button>
              </p>
            ) : !squad.settled ? (
              <p className="games-note">Loading your squad…</p>
            ) : side === 'checkins' ? (
              <CheckInsSection squad={squad} expanded={expanded.checkins} onToggle={() => setExpanded((e) => ({ ...e, checkins: !e.checkins }))} />
            ) : (
              <PredictionsSection event={event} squad={squad} youPredicted={youPredicted} expanded={expanded.predictions} onToggle={() => setExpanded((e) => ({ ...e, predictions: !e.predictions }))} />
            )}
          </>
        )}
        {error ? <p className="games-dialog-error">{error}</p> : null}
      </div>
      {footer ? (
        <div className="games-dialog-actions is-sheet-footer">
          <button type="button" className="games-btn is-primary" onClick={footer.onClick} disabled={footer.disabled}>
            {footer.label}
          </button>
        </div>
      ) : null}
    </Modal>
  );
}
